/* import __COLOCATED_TEMPLATE__ from './embercom-revision-banner.hbs'; */
/* RESPONSIBLE TEAM: team-frontend-tech */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import move from 'ember-animated/motions/move';
import { easeOut, easeIn } from 'ember-animated/easings/cosine';
import {
  getVersionFromMetaTag,
  getActiveRevisionFromMetaTag,
  getTeammateAppUrls,
  validateExperimentalUrls,
} from 'embercom/lib/react-islands';
import ENV from 'embercom/config/environment';

export default class EmbercomRevisionBanner extends Component {
  @tracked isCollapsed = false;
  @tracked _isValidTeammateAppRevision = null;

  constructor() {
    super(...arguments);
    this.validateTeammateAppRevision();
  }
  get hasEmbercom() {
    return !!this.args.embercomRevision;
  }
  get hasTeammateApp() {
    return !!this.args.teammateAppRevision;
  }
  get isValidEmbercomRevision() {
    if (!this.hasEmbercom) {
      return true;
    }
    let currentlyLoadedRevision = Ember.libraries._registry
      .find((library) => library.name === 'Embercom')
      .version.replace('0.0.0+', '');
    return this.args.embercomRevision.startsWith(currentlyLoadedRevision);
  }

  async validateTeammateAppRevision() {
    if (!this.hasTeammateApp) {
      this._isValidTeammateAppRevision = true;
      return;
    }
    let currentlyLoadedRevision = getVersionFromMetaTag();
    if (currentlyLoadedRevision?.endsWith('-experimental')) {
      let { jsUrl, cssUrl } = getTeammateAppUrls();
      let isValid = await validateExperimentalUrls(jsUrl, cssUrl);
      if (!isValid) {
        this._isValidTeammateAppRevision = false;
        await this.handleInvalidExperimentalRevision();
        return;
      }
    }
    this._isValidTeammateAppRevision = this.args.teammateAppRevision === currentlyLoadedRevision;
  }
  get isValidTeammateAppRevision() {
    return this._isValidTeammateAppRevision;
  }

  get shouldShowBanner() {
    return this._isValidTeammateAppRevision !== null;
  }

  async handleInvalidExperimentalRevision() {
    if (ENV.environment === 'development') {
      this.clearMetaTagContent();
      return;
    }

    let activeRevision = getActiveRevisionFromMetaTag();
    if (activeRevision) {
      this.setMetaTagContent(activeRevision);
    } else {
      console.error('No active teammate app revision found in meta tag');
    }
  }

  setMetaTagContent(version) {
    document
      .querySelector('meta[name="teammate_app_revision_version"]')
      .setAttribute('content', version);
  }

  clearMetaTagContent() {
    this.setMetaTagContent('');
  }

  get isValidRevision() {
    return this.isValidEmbercomRevision && this.isValidTeammateAppRevision;
  }
  get isEmbercomExperimentalBuild() {
    return this.args.embercomRevision?.includes('-experimental');
  }
  get isTeammateAppExperimentalBuild() {
    return this.args.teammateAppRevision?.includes('-experimental');
  }
  get embercomRevisionProcessed() {
    return this.args.embercomRevision?.replace('-experimental', '');
  }
  get teammateAppRevisionProcessed() {
    return this.args.teammateAppRevision?.endsWith('-experimental')
      ? this.args.teammateAppRevision.replace('-experimental', '')
      : this.args.teammateAppRevision;
  }

  get teammateAppRevisionUrl() {
    let baseUrl = 'https://github.com/intercom/embercom';
    let urlType = this.isTeammateAppExperimentalBuild ? 'tree' : 'commit';
    return `${baseUrl}/${urlType}/${this.teammateAppRevisionProcessed}`;
  }

  get embercomRevisionUrl() {
    let baseUrl = 'https://github.com/intercom/embercom';
    let urlType = this.isEmbercomExperimentalBuild ? 'tree' : 'commit';
    return `${baseUrl}/${urlType}/${this.embercomRevisionProcessed}`;
  }

  get bannerType() {
    return this.isValidRevision ? 'warning' : 'error';
  }
  @action toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
  }
  // eslint-disable-next-line require-yield
  *transition({ insertedSprites, keptSprites, removedSprites }) {
    for (let sprite of insertedSprites) {
      sprite.startAtPixel({ x: -window.innerWidth });
      move(sprite, { easing: easeOut });
    }

    for (let sprite of keptSprites) {
      move(sprite);
    }

    for (let sprite of removedSprites) {
      sprite.endAtPixel({ x: -window.innerWidth });
      move(sprite, { easing: easeIn });
    }
  }
}
